// Corrections found after the statements for one month went out.
//
// This sits BELOW the matrix rather than inside it, on purpose. The matrix shows
// stored figures and nothing else, which is why two screens in this app cannot
// disagree about a number. A write is not a stored figure: it is what we have
// since found to be true about a month already sent. Mixing the two into one
// grid would quietly turn "what the owner was told" into "what we now think",
// and those must stay separately readable.
//
// It exists because on 6 September R27,715 of private trips had been recognised
// against fourteen issued months and the month view showed none of it, so the
// honest answer to "where are my private trips" was "in a table no screen reads".
import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, CircleAlert } from 'lucide-react'
import { useGetStatementWrites } from '../../hooks/backend/statements'
import { Spinner } from '../../lib/shadcn/spinner'
import { formatZar } from '../../utils/statements'
import { cn } from '../../lib/shadcn/utils'

type Write = {
  id: string
  plate: string
  label: string
  line_code: string | null
  as_issued_zar: number
  true_zar: number
  delta_zar: number
  reason: string
  settled_period_start: string | null
  settlement_status: string
}

export function MonthWrites({ periodStart }: { periodStart: string }) {
  const query = useGetStatementWrites<Write[]>()
  const { trigger } = query
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!periodStart) return
    setOpen(false)
    trigger({ period_start: periodStart }).catch(() => {})
  }, [trigger, periodStart])

  const writes = query.data ?? []

  const byLine = useMemo(() => {
    const m = new Map<string, { label: string; total: number; count: number }>()
    for (const w of writes) {
      const key = w.line_code ?? w.label
      const e = m.get(key) ?? { label: w.label, total: 0, count: 0 }
      e.total += w.delta_zar
      e.count += 1
      m.set(key, e)
    }
    return Array.from(m.values()).sort((a, b) => Math.abs(b.total) - Math.abs(a.total))
  }, [writes])

  if (query.loading) {
    return (
      <div className="flex items-center gap-2 border-t border-border px-4 py-2 text-xs text-muted-foreground">
        <Spinner className="h-3.5 w-3.5" /> Checking for corrections on this month
      </div>
    )
  }
  if (query.error || writes.length === 0) return null

  const net = writes.reduce((s, w) => s + w.delta_zar, 0)
  const unsettled = writes.filter((w) => !w.settled_period_start).reduce((s, w) => s + w.delta_zar, 0)
  const vehicles = new Set(writes.map((w) => w.plate)).size

  return (
    <div className="border-t border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-muted/50"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="text-sm font-semibold">Corrections since these statements went out</span>
        <span className="text-xs text-muted-foreground">
          {writes.length} on {vehicles} vehicle{vehicles === 1 ? '' : 's'}
        </span>
        <span
          className={cn(
            'ml-auto tabular-nums text-sm font-semibold',
            net < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400',
          )}
        >
          {formatZar(net)}
        </span>
      </button>

      {/* Collapsed still shows the summary by line, because the question this
          answers most often is "where did the private trips go", and that should
          not need a click. */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 px-4 pb-2 pl-10 text-xs text-muted-foreground">
        {byLine.map((l) => (
          <span key={l.label}>
            {l.label}{' '}
            <span
              className={cn(
                'tabular-nums font-medium',
                l.total < 0
                  ? 'text-rose-600 dark:text-rose-400'
                  : 'text-emerald-600 dark:text-emerald-400',
              )}
            >
              {formatZar(l.total)}
            </span>
          </span>
        ))}
      </div>

      {unsettled !== 0 && (
        <div className="flex items-center gap-2 px-4 pb-2 pl-10 text-xs text-amber-700 dark:text-amber-400">
          <CircleAlert className="h-3.5 w-3.5 shrink-0" />
          <span>
            <strong className="tabular-nums">{formatZar(unsettled)}</strong> recognised with no open
            statement to settle it on, because those vehicles have left the fleet.
          </span>
        </div>
      )}

      {open && (
        <div className="max-h-80 overflow-y-auto border-t border-border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card">
              <tr className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-1.5 text-left font-semibold">Vehicle</th>
                <th className="px-2 py-1.5 text-left font-semibold">Line</th>
                <th className="px-2 py-1.5 text-right font-semibold">As sent</th>
                <th className="px-2 py-1.5 text-right font-semibold">Should have read</th>
                <th className="px-2 py-1.5 text-right font-semibold">Difference</th>
                <th className="px-4 py-1.5 text-left font-semibold">Settled</th>
              </tr>
            </thead>
            <tbody>
              {writes.map((w) => (
                <tr key={w.id} className="border-b border-border/60">
                  <td className="px-4 py-1.5 font-medium">{w.plate}</td>
                  <td className="px-2 py-1.5">{w.label}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                    {formatZar(w.as_issued_zar)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{formatZar(w.true_zar)}</td>
                  <td
                    className={cn(
                      'px-2 py-1.5 text-right tabular-nums font-medium',
                      w.delta_zar < 0
                        ? 'text-rose-600 dark:text-rose-400'
                        : 'text-emerald-600 dark:text-emerald-400',
                    )}
                  >
                    {formatZar(w.delta_zar)}
                  </td>
                  <td
                    className={cn(
                      'px-4 py-1.5',
                      w.settled_period_start
                        ? 'text-muted-foreground'
                        : 'font-medium text-amber-600 dark:text-amber-400',
                    )}
                  >
                    {w.settlement_status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
